import { paymentSystemLabel } from '@bemor/web-kit';
import {
    AsOfBadge,
    CardBrand,
    DateTime,
    ObjectRef,
    PageHeader,
    PaymentSystemIcon,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { StaffPaymentMethod } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/payment-methods';
const PAGE_KEY = 'payment-methods';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const paymentMethodsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const paymentMethodFilters = filterDefs(PATH, {
    merchant_id: 'Merchant',
    customer_ref: 'Customer ref',
    status: 'Status',
    type: 'Payment system',
    provider: 'Provider',
    offered_to_buyer: 'Show to customer',
    range: 'Created',
});

/** The payment method page, where the charge page's saved method links (bm-111 § 21). */
export const paymentMethodHref = (id: string) =>
    `/payment-methods/${encodeURIComponent(id)}`;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The card as the buyer sees it: brand, last 4 and expiry. */
export function MethodCard({ method: m }: { method: StaffPaymentMethod }) {
    return (
        <span className="inline-flex items-center gap-2">
            <CardBrand brand={m.brand} />
            <span className="font-mono text-sm">
                •••• {m.last4} · {pad2(m.exp_month)}/{m.exp_year}
            </span>
        </span>
    );
}

/** The payment system: its icon and name over the provider's label and account. */
export function MethodSystem({ method: m }: { method: StaffPaymentMethod }) {
    const account = m.payment_system.provider_account;
    return (
        <span className="flex min-w-0 flex-col">
            <span className="inline-flex items-center gap-2">
                <PaymentSystemIcon system={m.type} />
                {paymentSystemLabel(m.type)}
            </span>
            <span className="text-muted-foreground text-sm">
                {m.payment_system.label ?? m.payment_system.provider}
                {account ? ` · ${account.name ?? account.id}` : ''}
            </span>
        </span>
    );
}

/** The method's customer: the name over the email and reference, a dash when stored under none. */
export function MethodCustomer({ method: m }: { method: StaffPaymentMethod }) {
    const c = m.customer;
    if (!c) return <>—</>;
    return (
        <span className="flex min-w-0 flex-col">
            {c.name ? <span>{c.name}</span> : null}
            {c.email ? (
                <span className="text-muted-foreground text-sm break-all">
                    {c.email}
                </span>
            ) : null}
            <span className="text-muted-foreground font-mono text-xs break-all">
                {c.customer_ref}
            </span>
        </span>
    );
}

/** Whether the hosted page's saved-card picker lists the method for its customer. */
export const offeredLabel = (m: StaffPaymentMethod) =>
    m.offered_to_buyer ? 'Yes' : 'No';

/** Whether the method is its customer's default, read on the list cell and the method page alike. */
export const defaultLabel = (m: StaffPaymentMethod) =>
    m.is_default ? 'Yes' : 'No';

/**
 * bm-111 § 21 (D-18): every merchant's saved payment methods, newest first: merchant, customer, payment system, the
 * card as the buyer sees it, BIN, issuer country, status, is default and show to customer (whether the hosted page
 * shows it to the buyer). A phone card keeps the card, status and show to customer; the rest is on the method page the row links to.
 */
export function PaymentMethodsPage() {
    const columns: ListColumnDef<StaffPaymentMethod>[] = [
        {
            key: 'created_at',
            aliases: ['created'],
            label: 'Created',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (r) => r.created_at,
            cell: (r) => <DateTime value={r.created_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'id',
            label: 'Payment method',
            cell: (r) => (
                <Link
                    to={paymentMethodHref(r.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {r.id}
                </Link>
            ),
            csv: (r) => r.id,
            noTruncate: true,
        },
        {
            key: 'merchant',
            label: 'Merchant',
            csv: (r) => r.merchant.id,
            cell: (r) => (
                <span className="flex min-w-0 flex-col">
                    {r.merchant.name ? <span>{r.merchant.name}</span> : null}
                    <ObjectRef id={r.merchant.id} />
                </span>
            ),
        },
        {
            key: 'customer',
            label: 'Customer',
            csv: (r) => r.customer?.customer_ref ?? '',
            cell: (r) => <MethodCustomer method={r} />,
        },
        {
            key: 'payment_system',
            aliases: ['type'],
            label: 'Payment system',
            csv: (r) => r.type,
            cell: (r) => <MethodSystem method={r} />,
        },
        {
            key: 'card',
            label: 'Card',
            csv: (r) => `${r.brand} ${r.last4}`,
            boardCard: true,
            cell: (r) => <MethodCard method={r} />,
            noTruncate: true,
        },
        {
            key: 'bin',
            label: 'BIN',
            csv: (r) => r.bin,
            cell: (r) => <span className="font-mono text-sm">{r.bin}</span>,
        },
        {
            key: 'issuer_country',
            label: 'Issuer country',
            csv: (r) => r.issuer_country ?? '',
            cell: (r) => r.issuer_country ?? '—',
        },
        {
            key: 'status',
            label: 'Status',
            csv: (r) => r.status,
            boardCard: true,
            cell: (r) => <StatusBadge status={r.status} />,
        },
        {
            key: 'is_default',
            aliases: ['default'],
            label: 'Is Default',
            csv: (r) => (r.is_default ? 'yes' : 'no'),
            cell: (r) => (
                <span data-testid="method-default">{defaultLabel(r)}</span>
            ),
        },
        {
            key: 'offered_to_buyer',
            label: 'Show to customer',
            csv: (r) => (r.offered_to_buyer ? 'yes' : 'no'),
            boardCard: true,
            cell: (r) => (
                <span data-testid="method-offered">{offeredLabel(r)}</span>
            ),
        },
        {
            key: 'updated_at',
            aliases: ['updated'],
            label: 'Updated',
            kind: 'datetime',
            csv: (r) => r.updated_at ?? '',
            cell: (r) => <DateTime value={r.updated_at} empty="—" />,
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<StaffPaymentMethod>(PAGE_KEY, PATH, {
        columns,
        filters: paymentMethodFilters,
    });
    return (
        <div>
            <PageHeader
                title="Payment methods"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Saved payment methods across merchants, and whether
                            the hosted page shows each to its buyer.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="payment methods"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                rowHref={(r) => paymentMethodHref(r.id)}
                mobileCard={{
                    fields: ['card', 'status', 'offered_to_buyer'],
                }}
            />
        </div>
    );
}
